import { useState } from "react";
import { Copy, Loader2, Star, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

// Popup « Témoignage → −5 % » du panier (demande du propriétaire, 2026-10-07) :
// juste avant le paiement, le client raconte son expérience → code unique
// MERCI-XXXXXX (−5 % sur le total avant taxes, usage unique, 7 jours).
// CONFORME GOOGLE : la récompense vise le témoignage, JAMAIS l'avis Google ;
// après l'envoi, on propose « Publier aussi sur Google » (texte copié d'un
// clic, formulaire Google ouvert — facultatif, sans récompense).
export function TemoignageDialog({ open, onClose, onCode, lang }) {
  const fr = lang !== "en";
  const [nom, setNom] = useState("");
  const [texte, setTexte] = useState("");
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fait, setFait] = useState(null);   // {code, google_review_url}

  const envoyer = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shop/temoignage", {
        nom: nom.trim(), texte: texte.trim(), consentement: consent,
      });
      setFait(data);
      onCode(data.code);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || (fr ? "Envoi impossible — réessayez." : "Could not send — please retry."));
    } finally {
      setBusy(false);
    }
  };

  const fermer = () => { setFait(null); setNom(""); setTexte(""); setConsent(false); onClose(); };

  const copierPuisGoogle = async () => {
    try {
      await navigator.clipboard.writeText(texte.trim());
      toast.success(fr ? "Témoignage copié — collez-le dans le formulaire Google" : "Testimonial copied — paste it into the Google form");
    } catch { /* presse-papiers refusé : le client copiera à la main */ }
    window.open(fait.google_review_url, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) fermer(); }}>
      <DialogContent className="bg-surface border border-gold/25 text-ivory max-w-lg" data-testid="temoignage-dialog">
        {!fait ? (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl flex items-center gap-2">
                <Star className="w-5 h-5 text-gold" />
                {fr ? "Racontez votre expérience — −5 %" : "Share your experience — 5% off"}
              </DialogTitle>
              <DialogDescription className="text-ivory/60 text-sm">
                {fr
                  ? "Quelques phrases sur votre expérience avec Mr Trophée, et un rabais de 5 % s'applique à votre commande (total avant taxes). Votre témoignage pourra être publié sur notre site."
                  : "A few words about your experience with Mr Trophée, and 5% off applies to your order (pre-tax total). Your testimonial may be published on our site."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <label className="block">
                <span className="block text-[10px] uppercase tracking-widest text-ivory/60 mb-1.5">{fr ? "Votre nom (ou prénom + ville)" : "Your name (or first name + city)"}</span>
                <input value={nom} onChange={(e) => setNom(e.target.value)} maxLength={80}
                  data-testid="temoignage-nom" placeholder={fr ? "ex. Marie, Léry" : "e.g. Mary, Léry"}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none transition-colors" />
              </label>
              <label className="block">
                <span className="block text-[10px] uppercase tracking-widest text-ivory/60 mb-1.5">{fr ? "Votre témoignage" : "Your testimonial"}</span>
                <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={4} maxLength={600}
                  data-testid="temoignage-texte"
                  placeholder={fr ? "ex. Service rapide, belle qualité de gravure, prix imbattables…" : "e.g. Fast service, great engraving quality, unbeatable prices…"}
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-2.5 text-sm focus:border-gold focus:outline-none transition-colors resize-none" />
                <span className="block text-right text-[10px] text-ivory/40 mt-1">{texte.trim().length}/600 · {fr ? "20 caractères min." : "20 characters min."}</span>
              </label>
              <label className="flex items-start gap-2.5 text-xs text-ivory/70 cursor-pointer">
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  data-testid="temoignage-consent" className="accent-[#D4AF37] w-4 h-4 mt-0.5 shrink-0" />
                {fr
                  ? "J'autorise Mr Trophée à publier mon témoignage (avec mon nom) sur son site Web."
                  : "I allow Mr Trophée to publish my testimonial (with my name) on their website."}
              </label>
              <button onClick={envoyer}
                disabled={busy || nom.trim().length < 2 || texte.trim().length < 20 || !consent}
                data-testid="temoignage-submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold px-6 py-3.5 rounded-full text-sm hover:bg-champagne transition-colors duration-300 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />}
                {fr ? "Recevoir mon rabais de 5 %" : "Get my 5% discount"}
              </button>
            </div>
          </>
        ) : (
          <div className="text-center py-4" data-testid="temoignage-merci">
            <span className="inline-flex w-14 h-14 rounded-full bg-gold/15 border border-gold/30 items-center justify-center mb-4">
              <Check className="w-7 h-7 text-gold" />
            </span>
            <h3 className="font-serif text-2xl mb-2">{fr ? "Merci !" : "Thank you!"}</h3>
            <p className="text-sm text-ivory/70 mb-1">
              {fr ? "Votre rabais de 5 % est appliqué à votre panier :" : "Your 5% discount is applied to your cart:"}
            </p>
            <p className="font-mono text-gold text-lg font-bold mb-5" data-testid="temoignage-code">{fait.code}</p>
            <p className="text-xs text-ivory/55 mb-5">
              {fr
                ? "Envie d'en faire profiter d'autres ? Copiez votre témoignage et collez-le comme avis Google — ça prend 30 secondes :"
                : "Want to spread the word? Copy your testimonial and paste it as a Google review — it takes 30 seconds:"}
            </p>
            <button onClick={copierPuisGoogle} data-testid="temoignage-google"
              className="w-full inline-flex items-center justify-center gap-2 border border-gold/50 text-gold font-semibold px-6 py-3 rounded-full text-sm hover:bg-gold/10 transition-colors duration-300 mb-3">
              <Copy className="w-4 h-4" />
              {fr ? "Copier et publier aussi sur Google" : "Copy and also post on Google"}
            </button>
            <button onClick={fermer} data-testid="temoignage-close"
              className="text-xs text-ivory/50 hover:text-gold underline underline-offset-2 transition-colors">
              {fr ? "Non merci, passer au paiement" : "No thanks, continue to checkout"}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
